import { fieldMappings, excludedFields, compositeFields, conditionMap, gradeMap, CASE_REVIEW_COLUMNS } from '../../../common/mappings';
import { generateErrorMessages, checkCompErrors, createErrorsArray } from '../../../utils/compErrorFlagging';
import { getIFMVUtil, getAVUtil } from '../../../utils/getAVUtil';
import { compRowId, normalizeCompId } from '../common/compSheetLookup';

// Helper function to handle string values
const handleStringValue = (value, defaultValue = '') => {
  if (value === null || value === undefined) return defaultValue;
  return String(value);
};

const calculateLoa = (salePrice, assessment) => {
  const sale = Number(String(salePrice ?? '').replace(/[,$]/g, ''));
  const assess = Number(String(assessment ?? '').replace(/[,$]/g, ''));
  if (!sale || !assess || isNaN(sale) || isNaN(assess)) {
    return '';
  }
  return (assess / sale) * 100;
};

// Helper function to create property info object
const createPropertyInfo = (comp) => {
  return {
    Address: comp.Address || '',
    SDName: comp.SDName || '',
    SDCode: comp.SDCode || '',
    parcel_id: comp.parcel_id || comp.PID || comp.ParcelID || comp.id || '',
    Town: comp.Town || '',
    ZipCode: comp.ZipCode || '',
  };
};

// Centralized function to process a single row with all mappings and formatters
export const processRowData = (rowData, specialFormatters, handleNumericValue) => {
  // Add totalBaths key if it doesn't exist so it can be processed in field mapping
  if (rowData.totalBaths === undefined) {
    rowData.totalBaths = undefined; // Will be calculated in field mapping
  }

  const result = {
    ...rowData,
    ...Object.entries(fieldMappings).reduce((acc, [dbField, uiField]) => {
      if (!excludedFields.includes(dbField)) {
        // Special case for totalBaths - it's a calculated field, not from input data
        if (uiField === 'totalBaths') {
          acc[uiField] = specialFormatters[uiField](
            rowData.Bath ?? rowData.BathsFull ?? 0,
            rowData.HalfBath ?? rowData.BathsHalf ?? 0
          );
        } else if (rowData[dbField] !== undefined) {
          // Apply special formatters for specific fields
          if (specialFormatters[uiField]) {
            if (uiField === 'View') {
              acc[uiField] = specialFormatters[uiField](
                rowData.Waterfront,
                rowData.Waterview
              );
            } else {
              acc[uiField] = specialFormatters[uiField](rowData[dbField]);
            }
          } else {
            // Apply normalization for condition and grade fields
            if (uiField === 'Condition') {
              acc[uiField] = conditionMap[rowData[dbField]] || rowData[dbField] || 'Unknown';
            } else if (uiField === 'Grade') {
              acc[uiField] = gradeMap[rowData[dbField]] || rowData[dbField] || 'Unknown';
            } else {
              // Use handleNumericValue for fields that should display 0 instead of empty string
              const numericFields = ['fullBaths', 'halfBaths', 'Bedrooms', 'TotalGarageCap', 'FinishedBasement', 'Fireplaces', 'TotalRooms'];
              if (numericFields.includes(uiField)) {
                acc[uiField] = handleNumericValue(rowData[dbField], '');
              } else {
                acc[uiField] = rowData[dbField] || '';
              }
            }
          }
        }
      }
      return acc;
    }, {})
  };

  if (result.ALFlag === 'N' || result.ALFlag === 'n') result.ALFlag = 0;
  else if (result.ALFlag === 'Y' || result.ALFlag === 'y') result.ALFlag = 1;

  return result;
};


// Main row construction function - simplified to work like the original
export const createRows = ({
  comps,
  view,
  savedComps,
  selectedComps,
  subject,
  IS_SALE_RUN = false,
  compAdjPriceField,
  specialFormatters,
  handleNumericValue,
  getAdjustments = null,
  userFilterSettings = {}
}) => {
  // Handle negotiation / town views where comps is an array of row objects
  if ((view === 'negotiation' || view === 'town') && Array.isArray(comps)) {
    const resultRows = [...comps];
    // if (view === 'town') {
    //   return resultRows;
    // }
    // Add blank row at the end (negotiation only)
    resultRows.push({
      id: 'new-comp',
      '#': comps.length + 1,
      ALFlag: '',
      Acres: '',
      Address: '',
      AssessmentTown: '',
      Attribution: '',
      BadIFMV: false,
      Barns: '',
      BarnsSqFT: '',
      BasementType: '',
      BathsFull: '',
      BathsHalf: '',
      BayHarbor: '',
      BeachAccess: '',
      BeachFront: '',
      Bedrooms: '',
      BridgeView: '',
      BuildingStyle: '',
      CanalAccess: '',
      CentralAir: '',
      CityView: '',
      ConditionIn: '',
      Creek: '',
      Deck: '',
      Distance: '',
      EffYearBuilt: '',
      FinishedBasement: '',
      FinishedRecRoom: '',
      Fireplaces: '',
      GarageCapacity: '',
      Grade: '',
      Harbor: '',
      HeatType: '',
      IFMVTown: '',
      InGroundPool: '',
      Inlet: '',
      Kitchens: '',
      Lake: '',
      LakeFront: '',
      LakePondStream: '',
      LakePrivileges: '',
      LakeView: '',
      LastMLSSaleDate: '',
      Latitude: '',
      LivingArea: '',
      Longitude: '',
      LotSquareFeet: '',
      MLSNumber: '',
      MountainView: '',
      NumStories: '',
      OceanAccess: '',
      OpenView: '',
      OtherView: '',
      OtherWF: '',
      OutsideDateRange: 0,
      PanoramicView: '',
      ParcelID: '',
      ParkGreenbeltView: '',
      Patio: '',
      Pond: '',
      Porch: '',
      PorchArea: '',
      PropertyClass: '',
      RegridBldgFtPrnt: '',
      // RegridConvertedSqFt: '',
      RiverAccess: '',
      RiverView: '',
      SDCode: '',
      SDName: '',
      SaleIssue: '',
      SaleDate: '',
      SalePrice: '',
      SecondaryStyle: '',
      SkylineView: '',
      Sound: '',
      TimeAdjSalePrice: '',
      TotalRooms: '',
      Town: '',
      WaterAccess: '',
      Waterfront: '',
      Waterview: '',
      YearBuilt: '',
      Zip5: '',
      errors: [],
      notMLS: 0,
      parcel_id: '',
      returnWindow: 1,
      priceAdjustments: {},
      selected: false,
      selectedRowNumber: 0,
      status: '',
      time_adj: '',
      totalBaths: ''
    });
    return resultRows;
  }

  if (!comps || !comps.parcel_id || !Array.isArray(comps.parcel_id)) {
    return [];
  }

  // Missing comps: TransactionID on sales, parcel_id on assessment (same as persisted Comps)
  const idsForMissingCheck = savedComps;
  const existingIds = new Set(
    (IS_SALE_RUN
      ? (comps.TransactionID || []).map(normalizeCompId)
      : (comps.parcel_id || []).map(normalizeCompId)
    ).filter(Boolean),
  );
  const missingComps = idsForMissingCheck.filter(
    (id) => id != null && id !== '' && !existingIds.has(normalizeCompId(id)),
  );

  const usingExplicitSelection = Array.isArray(selectedComps) && selectedComps.length > 0;
  const activeComps = usingExplicitSelection ? selectedComps : savedComps;
  const activeCompsSet = new Set(
    activeComps.map(normalizeCompId).filter(Boolean),
  );

  // Gather keys we need to iterate over
  const addressKeys = Object.keys(comps.Address || {});
  const resultRows = [];

  for (let i = 0; i < addressKeys.length; i++) {
    const key = addressKeys[i];
    const currentParcelId = comps.parcel_id?.[key];
    const currentTransactionId = comps.TransactionID?.[key];
    const rowId = compRowId(currentParcelId, currentTransactionId);
    const selectionId = IS_SALE_RUN && currentTransactionId != null && currentTransactionId !== ''
      ? currentTransactionId
      : currentParcelId;
    const selectionNorm = normalizeCompId(selectionId);
    let isSelected = Boolean(selectionNorm && activeCompsSet.has(selectionNorm));
    // Legacy sales Comps may still be parcel_ids — select only the first sheet row for that parcel
    if (!isSelected && IS_SALE_RUN && activeCompsSet.has(normalizeCompId(currentParcelId))) {
      isSelected = comps.parcel_id.indexOf(currentParcelId) === Number(key);
    }

    const newlyAddedComps = new Set(missingComps.map(normalizeCompId).filter(Boolean));
    const isNewlyAdded = newlyAddedComps.has(selectionNorm);
    const selectedRowNumber = isSelected
      ? activeComps.findIndex((id) => normalizeCompId(id) === selectionNorm) + 1
      : 0;

    // Build priceAdjustments object once per row — use sheet index (not parcel findIndex)
    // so duplicate parcels with different TransactionIDs keep their own adjustments
    const priceAdjustments = {};
    if (comps.price_adjustments) {
      const adjustmentIndex = Number(key);
      if (Number.isFinite(adjustmentIndex) && adjustmentIndex >= 0) {
        for (const adjKey in comps.price_adjustments) {
          if (adjKey === 'parcel_id' || 
              adjKey === 'Target Id' || 
              adjKey.toLowerCase().includes('distance')){
                continue;
              }
          
          const rawValue = comps.price_adjustments[adjKey]?.[adjustmentIndex];
          priceAdjustments[adjKey] = rawValue;
        }
      }
    }

    // Create property info
    const propertyInfo = specialFormatters.PropertyInfo({
      Address: comps.Address?.[key],
      parcel_id: currentParcelId,
      SDName: comps.SDName?.[key],
      SDCode: comps.SDCode?.[key]
    });

    // Create a row object for error checking - include all fields that might be needed for adjustments
    const rowData = {
      parcel_id: comps.parcel_id?.[key],
      ALFlag: comps.ALFlag?.[key],
      SaleIssue: comps.SaleIssue?.[key],
      BadIFMV: comps.BadIFMV?.[key],
      SaleDate: comps.SaleDate?.[key],
      SalePrice: comps.SalePrice?.[key],
      Latitude: comps.Latitude?.[key],
      Longitude: comps.Longitude?.[key],
      // Core property fields
      Acres: comps.Acres?.[key],
      Sqft: comps.Sqft?.[key],
      IFMV: comps.IFMV?.[key],
      YearBuilt: comps.YearBuilt?.[key],
      Bath: comps.Bath?.[key],
      HalfBath: comps.HalfBath?.[key],
      TotalGarageCap: comps.TotalGarageCap?.[key],
      CentralAir: comps.CentralAir?.[key],
      InGroundPool: comps.InGroundPool?.[key],
      Fireplaces: comps.Fireplaces?.[key],
      Kitchens: comps.Kitchens?.[key],
      Deck: comps.Deck?.[key],
      Patio: comps.Patio?.[key],
      Porch: comps.Porch?.[key],
      FinishedBasement: comps.FinishedBasement?.[key],
      ConditionIn: comps.ConditionIn?.[key],
      Grade: comps.Grade?.[key],
      Waterfront: comps.Waterfront?.[key],
      Waterview: comps.Waterview?.[key],
      Style: comps.Style?.[key],
      Bedrooms: comps.Bedrooms?.[key],
      Heating: comps.Heating?.[key],
      TotalRooms: comps.TotalRooms?.[key],
      NeighborhoodCode: comps.NeighborhoodCode?.[key],
      PropertyAssessment: comps.PropertyAssessment?.[key],
      Taxes: comps.Taxes?.[key],
      // Add any other fields that the error checking function might need
    };

    // Use error data from stream processing if available, otherwise do comprehensive error checking
    let errors = [];
    let errorMessages = [];
    
    // if (comps.errorMessages && comps.errorMessages[key] && comps.errorMessages[key].length > 0) {
    //   // Use stream error messages if available
    //   errorMessages = comps.errorMessages[key];
    //   errors = errorMessages.map(msg => msg.code);
    // } else {
      // Fallback to comprehensive error checking
      const errorResult = checkCompErrors(rowData, subject, userFilterSettings, getAdjustments, !IS_SALE_RUN);
      errors = errorResult.errorCodes;
      errorMessages = errorResult.errorMessages;
    // }

    const baseRow = {
      priceAdjustments: priceAdjustments,
      id: rowId || currentParcelId,
      '#': parseInt(key) + 1,
      PropertyInfo: propertyInfo,
      Address: propertyInfo.Address,
      selected: isSelected,
      selectedRowNumber: selectedRowNumber,
      isNewlyAdded: isNewlyAdded,
      Sqft: handleNumericValue(comps.Sqft?.[key], ''),
      EstSqft: handleNumericValue(comps.Sqft_pred?.[key], ''),
      RegridBldgFtPrnt: specialFormatters.RegridBldgFtPrnt(comps.RegridBldgFtPrnt?.[key]),
      // RegridConvertedSqFt: handleNumericValue(comps.RegridConvertedSqFt?.[key], ''),
      SDName: comps.SDName?.[key] || '',
      SDCode: comps.SDCode?.[key] || '',
      parcel_id: currentParcelId,
      // Include Sale Price and Sale Date for both sale and assessment runs
      SalePrice: handleNumericValue(comps.SalePrice?.[key], ''),
      LOA: calculateLoa(comps.SalePrice?.[key], comps.PropertyAssessment?.[key]),
      SaleDate: comps.SaleDate?.[key] || '',
      MLSNumber: comps.MLSNumber?.[key] || '',
      ContractDate: comps.ContractDate?.[key] || '',
      ...(IS_SALE_RUN
        ? {
            adj_price: handleNumericValue(comps.adj_price?.[key], ''),
            be_adj_price: Math.round(handleNumericValue(comps.adj_price?.[key],0), ''),
          }
        : {
            adj_IFMV: handleNumericValue(comps.adj_IFMV?.[key], ''),
            be_adj_IFMV: Math.round(handleNumericValue(comps.adj_IFMV?.[key], 0), ''),
          }),
      Pool: comps?.InGroundPool?.[key] === 1 ? 'True' : '',
      CentralAir: comps?.CentralAir?.[key] === 1 || comps?.CentralAir?.[key] === '1'
        ? 'True'
        : comps?.CentralAir?.[key] === 0 || comps?.CentralAir?.[key] === '0'
          ? 'False'
          : '',
      Deck: comps?.Deck?.[key] == null || comps?.Deck?.[key] === '' ? null : handleNumericValue(comps.Deck[key]),
      Patio: comps?.Patio?.[key] == null || comps?.Patio?.[key] === '' ? null : handleNumericValue(comps.Patio[key]),
      Porch: comps?.Porch?.[key] == null || comps?.Porch?.[key] === '' ? null : handleNumericValue(comps.Porch[key]),
      Style: comps?.Style?.[key] || '',
      IFMV: handleNumericValue(getIFMVUtil(comps, key), ''),
      PCC: comps?.PCC?.[key] || '',
      ZipCode: comps?.ZipCode?.[key] || '',
      time_adj: handleNumericValue(comps?.TimeAdjSalePrice?.[key], ''),
      View: comps.Waterfront?.[key] === 1
        ? 'Waterfront'
        : comps.Waterview?.[key] === 1
        ? 'Waterview'
        : '',
      ALFlag: (() => {
        const value = comps.ALFlag?.[key];
        if (value === 'N' || value === 'n') return 0;
        if (value === 'Y' || value === 'y') return 1;
        return value ?? '';
      })(),
      SaleIssue: comps.SaleIssue?.[key] ?? '',
      // Handle bathroom fields explicitly with fallback for different naming conventions
      // Use nullish coalescing (??) instead of || to preserve 0 values
      fullBaths: handleNumericValue(comps.Bath?.[key] ?? comps.BathsFull?.[key], ''),
      halfBaths: handleNumericValue(comps.HalfBath?.[key] ?? comps.BathsHalf?.[key], ''),
      // Calculate totalBaths directly with fallback for different naming conventions
      totalBaths: (() => {
        const bathValue = comps.Bath?.[key] ?? comps.BathsFull?.[key] ?? 0;
        const halfBathValue = comps.HalfBath?.[key] ?? comps.BathsHalf?.[key] ?? 0;
        return (bathValue ?? 0) + (halfBathValue ?? 0) / 2;
      })(),
      ...Object.entries(fieldMappings).reduce((acc, [dbField, uiField]) => {
        if (!excludedFields.includes(dbField)) {
          // Skip fields that are part of PropertyInfo or handled explicitly above
          if (!compositeFields.PropertyInfo.fields.includes(uiField) && 
              !['Sqft', 'EstSqft', 'RegridBldgFtPrnt', 'time_adj', 'ZipCode', /* 'RegridConvertedSqFt', */ 'Waterfront', 'Waterview', 'SDName', 'Pool', 'Deck', 'Patio', 'Porch', 'CentralAir', 'adj_IFMV', 'Style', 'IFMV', 'PCC', 'adj_price', 'SalePrice', 'ALFlag', 'fullBaths', 'halfBaths', 'totalBaths'].includes(uiField)) {
            if (specialFormatters[uiField]) {
              if (uiField === 'View') {
                acc[uiField] = specialFormatters[uiField](
                  comps.Waterfront?.[key],
                  comps.Waterview?.[key]
                );
              } else {
                acc[uiField] = specialFormatters[uiField](comps[dbField]?.[key]);
              }
            } else {
              // Use handleNumericValue for fields that should display 0 instead of empty string
              const numericFields = ['fullBaths', 'halfBaths', 'Bedrooms', 'TotalGarageCap', 'FinishedBasement', 'Fireplaces', 'TotalRooms'];
              if (numericFields.includes(uiField)) {
                acc[uiField] = handleNumericValue(comps[dbField]?.[key], '');
              } else {
                acc[uiField] = comps[dbField]?.[key] || '';
              }
            }
          }
        }
        return acc;
      }, {}),
      errors: errors,
      errorMessages: errorMessages,
    };

    // Ensure any CaseReview column with a dbField defined is populated on the row.
    // This lets new simple columns be added by updating CASE_REVIEW_COLUMNS only.
    CASE_REVIEW_COLUMNS.forEach(({ field, dbField }) => {
      if (!dbField) return;
      if (baseRow[field] !== undefined) return;
      const sourceCollection = comps[dbField];
      if (sourceCollection && Object.prototype.hasOwnProperty.call(sourceCollection, key)) {
        baseRow[field] = sourceCollection[key] ?? '';
      }
    });

    if (baseRow.MLSPhotoFolder === undefined && comps.MLSPhotoFolder?.[key] !== undefined) {
      baseRow.MLSPhotoFolder = comps.MLSPhotoFolder[key] ?? '';
    }
    if (baseRow.SalesWebScreenshot === undefined && comps.SalesWebScreenshot?.[key] !== undefined) {
      baseRow.SalesWebScreenshot = comps.SalesWebScreenshot[key] ?? '';
    }

    // Add status calculation for non-negotiation views
    if (view === 'negotiation') {
      resultRows.push(baseRow);
    } else {
      const compAdjField = compAdjPriceField;
      const adjVal = comps[compAdjField]?.[key] || 0;
      const subjVal = getIFMVUtil(subject);
      let status;
      if (adjVal >= subjVal) {
        const excess = adjVal - subjVal;
        const percentExcess = (excess / subjVal) * 100;
        status = percentExcess <= 3 ? 'weakNegative' : 'negative';
      } else {
        const difference = subjVal - adjVal;
        const percentDifference = (difference / subjVal) * 100;
        status = percentDifference <= 3 ? 'weakPositive' : 'positive';
      }

      resultRows.push({
        ...baseRow,
        status: status,
        TotalGarageCap: handleNumericValue(comps.TotalGarageCap?.[key], ''),
      });
    }
  }


  return resultRows;
};

// Helper function to create subject row
export const createSubjectRow = ({
  subject,
  specialFormatters,
  handleNumericValue
}) => {
 
  if (!subject) return null;

  const propertyInfo = createPropertyInfo(subject);
  const errors = createErrorsArray(subject);
  
  // Calculate total baths using special formatter
  const totalBaths = specialFormatters.totalBaths(subject.Bath, subject.HalfBath);
  
  // Build the base object with all explicitly set fields
  const baseRowObject = {
    id: 'subject',
    '#': 0,
    selected: false,
    selectedRowNumber: 0,
    
    // Property info
    PropertyInfo: propertyInfo,
    Address: propertyInfo.Address,
    SDName: propertyInfo.SDName,
    SDCode: propertyInfo.SDCode,
    parcel_id: propertyInfo.parcel_id,
    Town: propertyInfo.Town,
    ZipCode: propertyInfo.ZipCode,
    
    // Basic property details
    PCC: handleStringValue(subject.PCC),
    RepID: handleStringValue(subject.RepID),
    Style: handleStringValue(subject.Style),
    PropertyClass: handleStringValue(subject.PropertyClass),
    View: subject.Waterfront === 1 ? 'Waterfront' : subject.Waterview === 1 ? 'Waterview' : '',
    YearBuilt: handleNumericValue(subject.YearBuilt),
    Sqft: handleNumericValue(subject.Sqft),
    EstSqft: handleNumericValue(subject.Sqft_pred),
    RegridBldgFtPrnt: specialFormatters.RegridBldgFtPrnt(subject.RegridBldgFtPrnt),
    // RegridConvertedSqFt: handleNumericValue(subject.RegridConvertedSqFt),
    Acres: handleNumericValue(subject.Acres),
    Grade: handleStringValue(subject.Grade),
    ALFlag: subject.ALFlag ?? '',
    NeighborhoodCode: handleStringValue(subject.NeighborhoodCode),
    
    // Financial data
    Price: handleNumericValue(subject.SalePrice || subject.IFMV),
    AdjPrice: handleNumericValue(subject.adj_price),
    SalePrice: handleNumericValue(subject.SalePrice),
    SaleDate: subject.SaleDate || '',
    MLSNumber: subject.MLSNumber || '',
    ContractDate: subject.ContractDate || '',
    IFMV: handleNumericValue(getIFMVUtil(subject)),
    adj_IFMV: handleNumericValue(subject.adj_IFMV),
    PropertyAssessment: handleNumericValue(getAVUtil(subject)),
    LOA: calculateLoa(subject.SalePrice, getAVUtil(subject)),
    
    // Other data
    Distance: 0,
    Latitude: handleNumericValue(subject.Latitude),
    Longitude: handleNumericValue(subject.Longitude),
    totalBaths: handleNumericValue(totalBaths),
    TotalGarageCap: handleNumericValue(subject.TotalGarageCap),
    Kitchens: handleNumericValue(subject.Kitchens),
    PhotoURL: handleStringValue(subject.PhotoURL),
    MLSPhotoFolder: subject.MLSPhotoFolder ?? '',
    SalesWebScreenshot: subject.SalesWebScreenshot ?? '',
    
    // Adjustment-related fields
    CentralAir: subject?.CentralAir === 1 || subject?.CentralAir === '1'
      ? 'True'
      : subject?.CentralAir === 0 || subject?.CentralAir === '0'
        ? 'False'
        : '',
    Bedrooms: handleNumericValue(subject.Bedrooms),
    Pool: subject?.InGroundPool === 1 ? "True" : "None",
    InGroundPool: handleNumericValue(subject.InGroundPool),
    Fireplaces: handleNumericValue(subject.Fireplaces),
    Deck: subject?.Deck == null || subject?.Deck === '' ? null : handleNumericValue(subject.Deck),
    Patio: subject?.Patio == null || subject?.Patio === '' ? null : handleNumericValue(subject.Patio),
    Porch: subject?.Porch == null || subject?.Porch === '' ? null : handleNumericValue(subject.Porch),
    FinishedBasement: handleStringValue(subject.FinishedBasement),
    fullBaths: handleNumericValue(subject.Bath),
    halfBaths: handleNumericValue(subject.HalfBath),
    TotalRooms: handleNumericValue(subject.TotalRooms),
    
    // Time adjustment fields
    TimeAdjSalePrice: handleNumericValue(subject.TimeAdjSalePrice),
    time_adj: handleNumericValue(subject.TimeAdjSalePrice),
    
    // Errors
    errors: errors,
    errorMessages: generateErrorMessages(errors),
    
    // Subject flag
    isSubject: true,
  };
  
  // Derive explicitly set fields from the object keys
  const explicitlySetFields = new Set(Object.keys(baseRowObject));
  
  const row = {
    ...baseRowObject,
    
    // Additional fields from field mappings
    // Skip all explicitly set fields to prevent overwriting
    ...Object.entries(fieldMappings).reduce((acc, [dbField, uiField]) => {
      const isExcluded = excludedFields.includes(dbField);
      const isCompositeField = Object.keys(compositeFields).some(compositeKey => 
        compositeFields[compositeKey].fields && compositeFields[compositeKey].fields.includes(uiField)
      );
      const isExplicitlySet = explicitlySetFields.has(uiField);
      
      if (
        !isExcluded &&
        !isCompositeField &&
        !isExplicitlySet &&
        subject[dbField] !== undefined
      ) {
        acc[uiField] = subject[dbField];
      }
      return acc;
    }, {}),
  };
  CASE_REVIEW_COLUMNS.forEach(({ field, dbField, headerName }) => {
    if (!headerName || !dbField || row[field] !== undefined) return;
    if (subject[dbField] !== undefined) row[field] = subject[dbField] ?? '';
  });
  return row;
};

export default {
  createRows,
  createSubjectRow,
  processRowData
};
